import { useInfiniteQuery } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { flattenInfinitePages, getNextPageParam } from '@/lib/constants/pagination';
import { CustomAttributeFilterRequest } from '@/modules/common/types/dto/request/custom-attribute-request';
import { mapCustomAttributeFilters } from '@/modules/common/utils/custom-attribute-filters';

import { getGenerateBillsData } from '../../api';
import {
  BILLS_FILTER_KEY_MEMBER,
  BILLS_FILTER_KEY_PROJECT,
} from '../../constants/bills-filter-keys';

export const GENERATE_BILLS_DATA_QUERY_KEY = 'generateBillsData';

export const mapGenerateBillsDataFilters = (filters: FilterValues['values']) => {
  const apiFilters: {
    searchString?: string;
    billingPeriod?: string;
    memberIds?: number[];
    projectIds?: number[];
    customAttributeFilters?: CustomAttributeFilterRequest[];
  } = {};

  if (filters.searchString && typeof filters.searchString === 'string') {
    apiFilters.searchString = filters.searchString;
  }

  if (filters.billingPeriod && typeof filters.billingPeriod === 'string') {
    apiFilters.billingPeriod = filters.billingPeriod;
  }

  if (filters[BILLS_FILTER_KEY_MEMBER]) {
    const arr = Array.isArray(filters[BILLS_FILTER_KEY_MEMBER])
      ? filters[BILLS_FILTER_KEY_MEMBER]
      : [filters[BILLS_FILTER_KEY_MEMBER] as string];
    apiFilters.memberIds = arr.map(Number);
  }

  if (filters[BILLS_FILTER_KEY_PROJECT]) {
    const arr = Array.isArray(filters[BILLS_FILTER_KEY_PROJECT])
      ? filters[BILLS_FILTER_KEY_PROJECT]
      : [filters[BILLS_FILTER_KEY_PROJECT] as string];
    apiFilters.projectIds = arr.map(Number);
  }

  const customAttrFilters = mapCustomAttributeFilters(filters);
  if (customAttrFilters) {
    apiFilters.customAttributeFilters = customAttrFilters;
  }

  return apiFilters;
};

export const useGenerateBillsData = ({ filters }: { filters: FilterValues['values'] }) => {
  return useInfiniteQuery({
    queryKey: [GENERATE_BILLS_DATA_QUERY_KEY, filters],
    queryFn: ({ pageParam }) => {
      const apiFilters = mapGenerateBillsDataFilters(filters);
      return getGenerateBillsData({ cursor: pageParam, filters: apiFilters });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    // Opts out of the app-wide focus/absence revalidation: this screen refreshes via
    // `usePollingStaleness` + `applySmartRefresh`, which merges the new response
    // against the user's edits. A silent refetch would skip that merge.
    refetchOnWindowFocus: false,
    select: flattenInfinitePages,
  });
};
